/**
 * Client-side mirror of inventory-api's fulfillment gate: an Inventory Item
 * Master (InventoryItemMasterService, app/resources/inventory/services/item_master.py)
 * at a location that is also a warehouse.
 *
 * "Inventory Item Master" is not a table — it is a per-store projection. A
 * product can be fulfilled from a location only when ALL of these hold:
 *
 *   1. the product is a variant of an *active* Item Master,
 *   2. the location is a warehouse, and
 *   3. that product's StorePart at the location has a non-empty bin location.
 *
 * Conditions 1 and 3 are the Inventory Item Master itself; the backend no longer
 * counts the warehouse as part of it, since any location can have one. Condition
 * 2 is what fulfillment adds (FulfillmentCandidate.is_eligible and the
 * fulfillment-order creation check), which is why this mirror still checks
 * `locationIsWarehouse`. It lets the UI evaluate that gate up front and explain
 * *which* condition is unmet, instead of letting the user submit and bounce off
 * a backend error.
 *
 * Two divergences from the backend, by necessity:
 *
 *   - The backend distinguishes "no Item Master" from "Item Master is inactive"
 *     (two separate messages). The DemandRequest query only exposes the
 *     collapsed `hasActiveItemMaster` boolean, so this mirror emits a single
 *     combined message for both.
 *   - The backend also requires the location to be non-archived
 *     (`date_archived is None`). The InventoryLocation GraphQL type does not
 *     expose an archived flag, so this mirror approximates with `isWarehouse`
 *     alone. An archived location acting as an active fulfiller is not expected.
 */

export type InventoryItemMasterInput = {
	/**
	 * Product is a variant of an active Item Master. Collapses the backend's
	 * separate has_item_master && item_master_is_active conditions.
	 */
	hasActiveItemMaster: boolean,
	/** The fulfilling inventory location is a warehouse. */
	locationIsWarehouse: boolean,
	/** The product's bin location at the fulfilling location, if stocked there. */
	binLocation: string | null | undefined,
}

export type InventoryItemMasterStatus = {
	hasActiveItemMaster: boolean,
	locationIsWarehouse: boolean,
	hasBinLocation: boolean,
	/**
	 * True only when every condition holds — the fulfillment gate (backend
	 * `is_present` and `location_is_warehouse`).
	 */
	isPresent: boolean,
}

/** A bin location counts as present only when it is a non-whitespace string. */
export function hasBinLocation( binLocation: string | null | undefined ): boolean {
	return typeof binLocation === 'string' && binLocation.trim() !== ''
}

export function inventoryItemMasterStatus( input: InventoryItemMasterInput ): InventoryItemMasterStatus {
	const binPresent = hasBinLocation( input.binLocation )
	return {
		hasActiveItemMaster: input.hasActiveItemMaster,
		locationIsWarehouse: input.locationIsWarehouse,
		hasBinLocation: binPresent,
		isPresent: input.hasActiveItemMaster && input.locationIsWarehouse && binPresent,
	}
}

export type InventoryItemMasterDiagnosticContext = {
	productLabel: string,
	locationName: string,
}

/**
 * The reason a single product's Inventory Item Master is not present, in the
 * backend's precedence order (active master → warehouse → bin location), or
 * null when present. Mirrors the elif chain in
 * FulfillmentOrderCommand._validate_item_master_configuration.
 */
export function inventoryItemMasterDiagnostic(
	status: InventoryItemMasterStatus,
	{ productLabel, locationName }: InventoryItemMasterDiagnosticContext,
): string | null {
	if ( !status.hasActiveItemMaster ) {
		return `${productLabel} must have an active item master`
	}
	if ( !status.locationIsWarehouse ) {
		return `${locationName} is not a warehouse and cannot fulfill ${productLabel}`
	}
	if ( !status.hasBinLocation ) {
		return `${productLabel} has no bin location at ${locationName}`
	}
	return null
}
